import Color from 'color';
import formatDateIntl from 'date-fns/intlFormat/index';

import {
    COLOR_DARK,
    COLOR_LIGHT,
    DISABLED_IN_PREVIEW,
    PREVIEW_MODE,
    TRACKING_KEY,
    TRACKING_PROPERTIES_KEY,
} from './global/constants';
import getQueryParameter from './global/helper/get-query-parameter';

import type { Theme } from '$lib/types/Theme';
import type { TrackingProperty } from '$lib/types/TrackingProperty';

declare global {
    interface String {
        splice(start: number, length: number, replacement: string): string;
    }
}

// For personalization string replacement
String.prototype.splice = function (start: number, length: number, replacement: string) {
    return this.substr(0, start) + replacement + this.substr(start + length);
};

export const hexToRgb = (hex: string) => {
    // hex can be #123 or #112233
    const sanitizedHexString =
        hex.length === 4 ? '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3] : hex;

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(sanitizedHexString);

    if (!result) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    };
};

export const rgbaToRgb = (rgba: string) => {
    const colors = rgba.match(/\d+/g);

    if (!colors) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(colors[0], 16),
        g: parseInt(colors[1], 16),
        b: parseInt(colors[2], 16),
    };
};

export const isHexString = (color: string) => {
    return /^#([A-Fa-f0-9]{3}){1,2}$/i.test(color);
};

export const isRgbaString = (color: string) => {
    return /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/.test(color);
};

export const isDark = (color: string) => {
    if (color === 'transparent') {
        return false;
    }

    try {
        const [r, g, b] = Color(color).rgb().array();
        const yiq = (r * 2126 + g * 7152 + b * 722) / 10000;
        return yiq < 128;
    } catch {
        return false;
    }
};

export const getColorByLightness = (refColor = '#000000') => {
    if (refColor.includes('linear-gradient')) return COLOR_LIGHT;

    if (refColor === 'transparent') return COLOR_DARK;

    return Color(refColor).lightness() <= 60 ? COLOR_LIGHT : COLOR_DARK;
};

/**
 * Get cookie preferences from local storage
 */
export const getCookiePreferences = (campaignId: string, campaignVersionId: string) => {
    const LS = window.localStorage;
    const preferences = LS.getItem(`${TRACKING_KEY}${campaignId}.${campaignVersionId}`);

    if (preferences) {
        return JSON.parse(preferences);
    }

    return {};
};

/**
 * Check if preferences are present in LS or not
 */
export const getCookiePreferencesPresent = (campaignId: string, campaignVersionId: string) => {
    const LS = window.localStorage;
    return !!LS.getItem(`${TRACKING_KEY}${campaignId}.${campaignVersionId}`);
};

/**
 * Set cookie preferences in local storage
 */
export const setCookiePreferences = (
    campaignId: string,
    campaignVersionId: string,
    preferences: { [key: string]: any },
) => {
    const LS = window.localStorage;

    return LS.setItem(
        `${TRACKING_KEY}${campaignId}.${campaignVersionId}`,
        JSON.stringify(preferences),
    );
};

/**
 * Add Http to URLs without http://, https://, tel: or mailto: prefix
 */
export const createExternalUrl = (
    url: string,
    campaignId?: string,
    trackingProperties?: TrackingProperty[],
) => {
    const { utm, custom, thirdParty } = getQueryParameter();
    let savedTrackingProperties = {};
    let defaultTrackingProperties = {};
    let allTrackedData = {};

    // makes sure all tracked data is used instead of just the ones set in the tracking properties
    // the `getQueryParameter` only gets the query parameters that are set in the current URL
    // we need to get all query parameters which were tracked on funnel load
    if (typeof window !== 'undefined') {
        allTrackedData = window.analytics?.user?.()?.traits();
    }

    if (campaignId) {
        savedTrackingProperties = getAllTrackingProperties(campaignId);
    }

    if (trackingProperties) {
        defaultTrackingProperties = getDefaultTrackingProperties(trackingProperties);
    }

    const processedUrl = replaceCustomVariables(url, {
        ...defaultTrackingProperties,
        ...savedTrackingProperties,
        ...thirdParty,
        ...utm,
        ...custom,
        ...allTrackedData,
    });

    if (!/(^mailto:)|(^tel:)|(^sms:)|(^https?:\/\/)/i.test(url)) {
        return 'http://' + processedUrl;
    }

    return processedUrl;
};

const replaceCustomVariables = (
    url: string,
    customVariables: { [key: string]: string | (string | null)[] | null },
) => {
    Object.keys(customVariables).forEach((key) => {
        const value = customVariables[key];

        if (value) {
            url = url.replace(`@${key}`, PREVIEW_MODE ? DISABLED_IN_PREVIEW : value.toString());
        }
    });

    return url;
};

export const setTrackingProperty = (
    key: string,
    value: string | undefined,
    campaignId: string | undefined,
) => {
    const ST = window.sessionStorage;
    const personalization = JSON.parse(
        ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`) || '{}',
    );

    const updatedPersonalization = personalization ? { ...personalization } : {};

    updatedPersonalization[key] = value;

    ST.setItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`, JSON.stringify(updatedPersonalization));
};

export const getAllTrackingProperties = (campaignId: string) => {
    const ST = window.sessionStorage;
    const properties = ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`);

    if (!properties) return {};

    return JSON.parse(ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`) || '{}');
};

export const getDefaultTrackingProperties = (trackingProperties: TrackingProperty[]) => {
    const mappedTrackingProperties: { [fieldName: string]: string } = {};

    trackingProperties.forEach((property) => {
        mappedTrackingProperties[property.fieldName] = property.defaultValue;
    });

    return mappedTrackingProperties;
};

export const parseISODate = (str: string, locale: string) => {
    if (isIsoDate(str)) {
        const date = new Date(str);

        if (!isNaN(date.valueOf())) {
            return formatDateIntl(
                date,
                { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
                { locale },
            );
        }
    }

    return str;
};

const isIsoDate = (str: string) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;

    const d = new Date(str);

    return d.toISOString() === str; // valid date
};

export const getColor = (color: string, themeColor: string) => {
    return !color || color === 'transparent' ? themeColor : color;
};

export const insertIntoArray = (arr: any[], value: any) => {
    return arr.reduce((result, element, index, array) => {
        result.push(element);

        if (index < array.length - 1) {
            result.push(value);
        }

        return result;
    }, []);
};

export const getFontName = (theme: Theme, fallbackFontName = 'Roboto') => {
    const { typography, fontId } = theme;

    if (fontId) {
        return fontId;
    }

    return typography.includes(':') ? typography.split(':')[0] : fallbackFontName;
};
